import type { RunRequest } from "../sdk";
import { currentTriple, encodeTriple, decodeTriple, type VersionTriple } from "../sdk";

// Reproducible permalink, encoded in the URL FRAGMENT (after #) so it is never sent to a
// server and the page stays fully client-side (RFC-0008 §10: source goes in the fragment,
// never the query).
//
//  v=1  gallery run:  #v=1&k=<id>&seed=<seed>&n=<n>
//  v=2  any run incl. custom: adds &t=<sdk.shim.fdlibm>, &p=<params> and, for custom kernels,
//       &src=<deflate-raw + base64url of the kernel source>.
//
// Params are encoded as key:value pairs sorted by key (`k1:v1,k2:v2`) so the string is a
// deterministic function of the values — part of the (kernel, seed, n, params) identity.
// Decoding v=1 is preserved so old links keep working. Custom-kernel source is compressed
// with the platform CompressionStream (deflate-raw) and base64url-encoded; decode-and-run on
// load reproduces the run exactly.

export interface DecodedPermalink {
  req: RunRequest;
  triple: VersionTriple | null;
}

export async function encodePermalink(req: RunRequest): Promise<string> {
  const params = new URLSearchParams();
  params.set("v", "2");
  params.set("k", req.kernelId);
  params.set("seed", req.seed);
  params.set("n", String(req.n));
  params.set("t", encodeTriple(currentTriple()));
  const p = encodeParams(req.params);
  if (p) params.set("p", p);
  if (req.kernelId === "custom" && req.source) {
    params.set("src", await deflateToBase64url(req.source));
  }
  return `#${params.toString()}`;
}

export async function decodePermalink(hash: string): Promise<DecodedPermalink | null> {
  if (!hash || hash === "#") return null;
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const v = params.get("v");
  if (v !== "1" && v !== "2") return null;

  const kernelId = params.get("k");
  const seed = params.get("seed");
  const n = Number(params.get("n"));
  if (!kernelId || seed === null || !Number.isFinite(n) || n <= 0) return null;

  const triple = v === "2" ? decodeTriple(params.get("t") ?? "") : null;
  const kernelParams = decodeParams(params.get("p"));

  if (kernelId === "custom") {
    const src = params.get("src");
    if (!src) return null;
    let source: string;
    try {
      source = await inflateFromBase64url(src);
    } catch {
      return null;
    }
    return { req: { kernelId, seed, n, source, params: kernelParams }, triple };
  }

  return { req: { kernelId, seed, n, params: kernelParams }, triple };
}

// ── params: `k1:v1,k2:v2` sorted by key (deterministic, URL-safe) ──

function encodeParams(p: RunRequest["params"]): string {
  if (!p) return "";
  const keys = Object.keys(p).filter((k) => Number.isFinite(p[k])).sort();
  if (keys.length === 0) return "";
  // `:` and `,` are reserved separators; param keys are simple identifiers and values are
  // numbers, so no escaping is needed. Use a compact number form (no trailing zeros).
  return keys.map((k) => `${k}:${numStr(p[k]!)}`).join(",");
}

function decodeParams(s: string | null): Record<string, number> | undefined {
  if (!s) return undefined;
  const out: Record<string, number> = {};
  for (const pair of s.split(",")) {
    const idx = pair.indexOf(":");
    if (idx <= 0) continue;
    const key = pair.slice(0, idx);
    const val = Number(pair.slice(idx + 1));
    if (Number.isFinite(val)) out[key] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

// Compact, round-trip-stable number string (avoids 0.1 → "0.1000000001" surprises while
// preserving full double precision when needed).
function numStr(v: number): string {
  return String(v);
}

// ── deflate-raw + base64url, via the platform CompressionStream (no dependency) ──

async function deflateToBase64url(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const compressed = await streamThrough(bytes, new CompressionStream("deflate-raw"));
  return bytesToBase64url(compressed);
}

async function inflateFromBase64url(b64: string): Promise<string> {
  const compressed = base64urlToBytes(b64);
  const bytes = await streamThrough(compressed, new DecompressionStream("deflate-raw"));
  return new TextDecoder().decode(bytes);
}

async function streamThrough(
  input: Uint8Array,
  transform: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const stream = new Response(new Blob([input as BlobPart])).body!.pipeThrough(
    transform as TransformStream,
  );
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

function bytesToBase64url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlToBytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
